import { z } from 'zod4';

import { fixture } from './fixtures';

/**
 * RCS Schemas
 */

export enum DistributionMethod {
	Axomo = 'axomo',
	CashOut = 'cash_out',
	Custom = 'custom',
	Donation = 'donation',
	ExclusiveExperience = 'exclusive_experience',
	ExclusiveOther = 'exclusive_other',
	ExclusiveSwag = 'exclusive_swag',
	GiftCard = 'gift_card',
	Physical = 'physical',
}

export enum ItemType {
	Core = 'core',
	Exclusive = 'exclusive',
	Featured = 'featured',
	Swag = 'swag',
}

export enum Provider {
	Amazon = 'amazon',
	Bonusly = 'bonusly', // allowance boost
	BulkDonation = 'bulk_donation',
	Cadooz = 'cadooz', // deprecated
	Paypal = 'paypal', // deprecated
	Snappy = 'snappy',
	Tango = 'tango_card',
	Tremendous = 'tremendous',
}

export enum DenominationInactiveReasons {
	AdminDisabled = 'admin_disabled',
	Discontinued = 'discontinued',
	NeedsAttention = 'needs_attention',
	OutOfStock = 'out_of_stock',
	SystemDisabled = 'system_disabled',
}

export enum RedemptionState {
	New = 'new',
	Approved = 'approved',
	Fulfilled = 'fulfilled',
	Declined = 'declined',
	Refunded = 'refunded',
}

export enum RedemptionRecordState {
	New = 'new',
	ManualPending = 'manual_pending',
	ManualFulfilled = 'manual_fulfilled',
	Succeeded = 'succeeded',
	Failed = 'failed',
	Canceled = 'canceled',
	Retry = 'retry',
	OutOfStock = 'out_of_stock',
	PendingVoucher = 'pending_voucher',
	Expired = 'expired',
}

export enum RedemptionItemType {
	Axomo = 'axomo',
	BuiltIn = 'built_in',
	Custom = 'custom',
	Other = 'other',
}

export const coreCategoryKeys = [
	'apparel',
	'beauty_and_spa',
	'cash',
	'donation',
	'electronics',
	'entertainment_and_electronics',
	'experiences_and_travel',
	'food_delivery',
	'food_and_drink',
	'gaming',
	'general',
	'gifts_and_services',
	'grocery',
	'health_and_wellness',
	'home_and_garden',
	'points_boost',
	'prepaid_card',
	'sports',
	'travel',
] as const;

export type CoreCategoryKey = (typeof coreCategoryKeys)[number];

export const TemporarilyUnavailableReasons = [
	DenominationInactiveReasons.AdminDisabled,
	DenominationInactiveReasons.NeedsAttention,
	DenominationInactiveReasons.SystemDisabled,
];

export const Reward = z.object({
	id: z.string(),
	brandName: z.string(),
	distributionMethod: z.enum(DistributionMethod),
	imageAltText: z.string(),
	imageUrl: z.string(),
	name: z.string(),
	minAmountInPoints: z.number().min(0),
	maxAmountInPoints: z.number().min(0),
	label: z.enum(['give_more', 'promoted', 'suggested', 'popular', 'exclusive']).nullable(),
	provider: z.enum(Provider).nullable(),
	type: z.enum(ItemType),
	/**
	 * Whether this reward requires rewards billing to be enabled.
	 * Points Boost, Company Exclusives, and non-Axomo Swag return false.
	 * Gift cards, donations, cash out, and Axomo items return true.
	 */
	requiresRewardsBilling: z.boolean().optional().default(true),
	subCategory: z.string().nullable().optional(),
});

export enum VariantStatus {
	Active = 'active',
	Disabled = 'disabled',
	Inactive = 'inactive',
	OutOfStock = 'out_of_stock',
}

export enum RewardStatus {
	Active = 'active',
	Inactive = 'inactive',
	LimitReached = 'limit_reached',
	OutOfStock = 'out_of_stock',
}

export const RewardVariant = z.object({
	id: z.string(),
	name: z.string(),
	taxonomy: z.object({
		color: z.string().optional(),
		size: z.string().optional(),
	}),
	priceInPoints: z.object({
		amount: z.number(),
		formattedAmount: z.string(),
	}),
	priceInCurrency: z
		.object({
			baseAmount: z.number(),
			formattedBaseAmount: z.string(),
			formattedBaseAmountShort: z.string(),
			totalAmount: z.number(),
			formattedTotalAmount: z.string(),
			formattedTotalAmountShort: z.string(),
			currency: z.string(),
		})
		.nullable(),
	status: z.enum(VariantStatus),
	images: z.array(
		z.object({
			url: z.string(),
			altText: z.string(),
		})
	),
	details: z.object({
		description: z.string().nullable(),
		dimensions: z.string().nullable(),
		features: z.string().nullable(),
		materials: z.string().nullable(),
		weight: z.string().nullable(),
	}),
});

export const RewardDetail = z.object({
	id: z.string(),
	brandName: z.string(),
	categories: z.array(
		z.object({
			id: z.string(),
			name: z.string(),
			key: z.string(),
		})
	),
	collectShippingInfo: z.boolean(),
	description: z.string().nullable(),
	fulfillmentPartner: z.string().nullable(),
	itemName: z.string(),
	itemType: z.enum(ItemType),
	distributionMethod: z.enum(DistributionMethod),
	provider: z.enum(Provider).nullable(),
	requiresRewardsBilling: z.boolean(),
	status: z.enum(RewardStatus),
	variantType: z.enum(['amount', 'beverageType', 'color', 'flavor', 'scent', 'size', 'takeYourPick']),
	variants: z.array(RewardVariant),
	warnings: z.array(z.string()),
	website: z.string().nullable(),
});

/**
 * Regular Rewards Schemas
 */

export const RedemptionRecord = z.object({
	id: z.string(),
	amount: z.number(),
	createdAt: z.string(),
	state: z.enum(RedemptionRecordState),
	transactionFee: z.number().nullable(),
});

export const RewardRedemption = z.object({
	id: z.string(),
	aasmState: z.enum(RedemptionState),
	brandName: z.string(),
	claimUrl: z.string().nullable(),
	color: z.string().nullable(),
	createdAt: z.string(),
	donation: z.boolean(),
	gift: z.boolean(),
	giftCard: z.boolean(),
	gifteeEmail: z.string().nullable(),
	name: z.string(),
	priceInCurrency: z.number(),
	priceInPoints: z.number(),
	size: z.string().nullable(),
	catalogItem: z.object({
		id: z.string(),
		denomination: z
			.object({
				id: z.string(),
				optionName: z.string(),
				optionType: z.string(),
				quantity: z.number().nullable(),
				distributionMethod: z.enum(DistributionMethod),
				provider: z.enum(Provider),
			})
			.nullable(),
		imageUrl: z.string(),
		type: z.enum(RedemptionItemType),
	}),
	redemptionRecords: z.array(RedemptionRecord),
});

export const RewardRedemptionDetail = RewardRedemption.extend({
	shippingAddress: z
		.object({
			address1: z.string(),
			address2: z.string().nullable(),
			city: z.string(),
			state: z.string(),
			postalCode: z.string(),
			country: z.string(),
		})
		.nullable(),
});

/**
 * All schema type inferences
 */

export type RedemptionRecord = z.infer<typeof RedemptionRecord>;
export type RewardRedemption = z.infer<typeof RewardRedemption>;
export type RewardRedemptionDetail = z.infer<typeof RewardRedemptionDetail>;
export type Reward = z.infer<typeof Reward>;
export type RewardDetail = z.infer<typeof RewardDetail>;
export type RewardVariant = z.infer<typeof RewardVariant>;

/**
 * Helpers
 */

export const isPhysicalItem = (item: { item: { distributionMethod: DistributionMethod } }) => {
	return item.item.distributionMethod === DistributionMethod.Physical;
};

/**
 * Fixtures
 */

// Fixture functions using manual generators
export const redemptionRecordFixture = (overrides: Partial<RedemptionRecord> = {}): RedemptionRecord => ({
	id: fixture.id(),
	amount: fixture.int(100, 5000),
	createdAt: fixture.pastDate(7).toISOString(),
	state: RedemptionRecordState.Succeeded,
	transactionFee: fixture.boolean() ? fixture.int(5, 50) : null,
	...overrides,
});

export const rewardRedemptionFixture = (overrides: Partial<RewardRedemption> = {}): RewardRedemption => ({
	id: fixture.id(),
	aasmState: RedemptionState.Fulfilled,
	brandName: fixture.name('Brand'),
	claimUrl: fixture.boolean() ? fixture.url('claim') : null,
	color: fixture.boolean() ? 'Red' : null,
	createdAt: fixture.pastDate(30).toISOString(),
	donation: fixture.boolean(false),
	gift: fixture.boolean(false),
	giftCard: fixture.boolean(true),
	gifteeEmail: fixture.boolean() ? fixture.email('giftee') : null,
	name: fixture.name('Reward Item'),
	priceInCurrency: fixture.int(10, 100),
	priceInPoints: fixture.int(100, 1000),
	size: fixture.boolean() ? 'Medium' : null,
	catalogItem: {
		id: fixture.id(),
		denomination: fixture.boolean()
			? {
					id: fixture.id(),
					optionName: fixture.string('Option'),
					optionType: fixture.string('Type'),
					quantity: fixture.boolean() ? fixture.int(1, 100) : null,
					distributionMethod: DistributionMethod.GiftCard,
					provider: Provider.Tango,
				}
			: null,
		imageUrl: fixture.url('item-image'),
		type: RedemptionItemType.BuiltIn,
	},
	redemptionRecords: [redemptionRecordFixture()],
	...overrides,
});

export const rewardRedemptionDetailFixture = (overrides: Partial<RewardRedemptionDetail> = {}): RewardRedemptionDetail => ({
	...rewardRedemptionFixture(),
	shippingAddress: fixture.boolean()
		? {
				address1: fixture.string('123 Main St'),
				address2: fixture.string('Apt 4B'),
				city: fixture.name('City'),
				state: fixture.string('CA'),
				postalCode: fixture.string('12345'),
				country: fixture.string('US'),
			}
		: null,
	...overrides,
});

export const rewardVariantFixture = (overrides: Partial<RewardVariant> = {}): RewardVariant => ({
	id: fixture.id(),
	name: fixture.name('Variant'),
	taxonomy: {
		color: fixture.boolean() ? fixture.string('Blue') : undefined,
		size: fixture.boolean() ? fixture.string('Medium') : undefined,
	},
	priceInPoints: {
		amount: fixture.int(100, 1000),
		formattedAmount: `${fixture.int(100, 1000)} points`,
	},
	priceInCurrency: fixture.boolean()
		? {
				baseAmount: fixture.int(10, 100),
				formattedBaseAmount: `$${fixture.int(10, 100)}.00`,
				formattedBaseAmountShort: `$${fixture.int(10, 100)}`,
				totalAmount: fixture.int(10, 110),
				formattedTotalAmount: `$${fixture.int(10, 110)}.00`,
				formattedTotalAmountShort: `$${fixture.int(10, 110)}`,
				currency: 'USD',
			}
		: null,
	status: VariantStatus.Active,
	images: [
		{
			url: fixture.url('variant-image'),
			altText: fixture.string('Variant image'),
		},
	],
	details: {
		description: fixture.boolean() ? fixture.description('variant') : null,
		dimensions: fixture.boolean() ? fixture.string('10x10x5 inches') : null,
		features: fixture.boolean() ? fixture.description('features') : null,
		materials: fixture.boolean() ? fixture.string('Cotton') : null,
		weight: fixture.boolean() ? fixture.string('1 lb') : null,
	},
	...overrides,
});

export const rewardDetailFixture = (overrides: Partial<RewardDetail> = {}): RewardDetail => ({
	id: fixture.id(),
	brandName: fixture.name('Brand'),
	categories: [
		{
			id: fixture.id(),
			name: fixture.name('Category'),
			key: fixture.string('category'),
		},
	],
	collectShippingInfo: fixture.boolean(),
	description: fixture.boolean() ? fixture.description('reward') : null,
	fulfillmentPartner: fixture.boolean() ? fixture.string('Partner') : null,
	itemName: fixture.name('Item'),
	itemType: ItemType.Core,
	distributionMethod: DistributionMethod.GiftCard,
	provider: fixture.boolean() ? Provider.Tango : null,
	requiresRewardsBilling: true,
	status: RewardStatus.Active,
	variantType: 'amount',
	variants: [rewardVariantFixture()],
	warnings: [],
	website: fixture.boolean() ? fixture.url('website') : null,
	...overrides,
});
